import router from '@/router'
import { userService } from '@/_services'
import { requests, resetSessionExpiry } from '@/_helpers/requests'
import { cPartner } from '@/_models/index'

// In-flight hydration request, shared so concurrent callers (router guard +
// App bootstrap) don't fire duplicate `partner/me` calls.
let inFlight = null
// Bumped on logout so a `partner/me` response that resolves after logout is
// recognised as stale and can't repopulate the store / re-authenticate.
let hydrationGeneration = 0

function friendlyHydrationError(error) {
  const detail = error && error.message
  return detail
    ? `We couldn’t load your account details: ${detail}`
    : 'We couldn’t load your account details. Please try again.'
}

// eslint-disable-next-line import/prefer-default-export
export const authentication = {
  namespaced: true,
  state: {
    status: {},
    partner: null,
    auth: {},
    currentLocationIndex: 0,
    hydration: 'idle',
    hydrationError: null,
  },
  getters: {
    location: state => {
      if (!state.partner) {
        return null
      }
      const { locations } = state.partner
      if (!locations || locations.length < 1) {
        return null
      }
      return locations[state.currentLocationIndex] || locations[0]
    },
    locations: state => state.partner?.locations ?? [],
    organization: state => state.partner?.organization ?? null,
    effectiveRole: state => state.partner?.effectiveRole ?? null,
    isAdmin: state => state.partner?.effectiveRole === 'admin',
    // Staff can view the location but not edit it; admins can edit.
    canEditLocation: state => state.partner?.effectiveRole === 'admin',
    hydration: state => state.hydration,
    hydrationError: state => state.hydrationError,
  },
  actions: {
    login({ dispatch, commit }, { username, password }) {
      resetSessionExpiry()
      commit('loginRequest', { username })

      // Returned and re-rejected so the caller can show the actual failure and know when
      // the request is done. Two-arg then(), not .catch(): a failure while navigating after
      // a *successful* login must not commit loginFailure.
      // username is email in partner portal. Emails are all lowercase in backend.
      return userService.login(username.toLowerCase(), password)
        .then(
          auth => {
            commit('loginSuccess', auth)
            return dispatch('ensureHydrated', true).then(status => {
              const target = status === 'ready'
                ? '/'
                : { name: 'session-unavailable', query: { reason: 'hydration' } }
              // A redundant-navigation rejection here isn't a login failure.
              return router.push(target).catch(() => {})
            })
          },
          error => {
            commit('loginFailure', error)
            dispatch('alert/error', error, { root: true })
            return Promise.reject(error)
          },
        )
    },
    logout({ commit }) {
      inFlight = null
      hydrationGeneration += 1
      resetSessionExpiry()
      userService.logout()
      commit('logout')
    },
    /**
     * Loads partner/org/location from `partner/me`. Resolves to 'ready' or
     * 'error' (never rejects). Deduplicates concurrent calls; pass force=true
     * to re-fetch after login.
     */
    ensureHydrated({ state, commit }, force = false) {
      if (!force && state.hydration === 'ready') {
        return Promise.resolve('ready')
      }
      if (inFlight) {
        return inFlight
      }

      const generation = hydrationGeneration
      commit('hydrationRequest')
      inFlight = requests.get('partner/me')
        .then(response => {
          // Logout (or another reset) happened while this was in flight; drop
          // the response so it can't restore partner and loggedIn = true.
          if (generation !== hydrationGeneration) {
            return 'stale'
          }
          commit('updatePartnerSuccess', response.data)
          return 'ready'
        })
        .catch(error => {
          if (generation !== hydrationGeneration) {
            return 'stale'
          }
          commit('hydrationFailure', friendlyHydrationError(error))
          return 'error'
        })
        .finally(() => {
          // Only clear if a newer request hasn't already replaced it.
          if (generation === hydrationGeneration) {
            inFlight = null
          }
        })
      return inFlight
    },
    switchLocation({ commit }, locationIndex) {
      commit('setCurrentLocationIndex', locationIndex)
    },
  },
  mutations: {
    loginRequest(state, username) {
      state.status = { loggingIn: true }
      state.username = username
    },
    loginSuccess(state, auth) {
      state.status = { loggedIn: true }
      state.auth = auth
    },
    loginFailure(state) {
      state.status = {}
      state.partner = null
    },
    hydrationRequest(state) {
      state.hydration = 'loading'
      state.hydrationError = null
    },
    updatePartnerSuccess(state, partner) {
      state.partner = cPartner.fromJSON(partner)
      state.status = { loggedIn: true }
      state.hydration = 'ready'
      state.hydrationError = null
    },
    hydrationFailure(state, message) {
      state.hydration = 'error'
      state.hydrationError = message
    },
    changedDp(state, preference) {
      state.partner.dashboardPreference = preference
    },
    logout(state) {
      state.status = {}
      state.partner = null
      state.currentLocationIndex = 0
      state.hydration = 'idle'
      state.hydrationError = null
    },
    setCurrentLocationIndex(state, index) {
      state.currentLocationIndex = index
    },
  },
}
